import React from 'react';
import { Button } from '../ui/button';
import { CheckCircle, AlertCircle, ChevronLeft, ChevronRight, FileQuestion } from 'lucide-react';
import ReportButton from '../ReportButton';
import { getEmbedUrl, isDirectVideoFile, buildAssetUrl } from './LessonPlayer';

// LinkedIn-Learning-style theater: big persistent player up top with
// prev / complete-&-next / quiz controls directly underneath.
export const TheaterPlayer = ({ lesson, onComplete, onStartQuiz, onPrev, onNext, hasPrev, hasNext }) => (
  <div className="bg-black rounded-xl overflow-hidden border border-white/10" data-testid="theater-player">
    <div className="aspect-video bg-black relative">
      {lesson?.content_type === 'video' && lesson.video_url ? (
        isDirectVideoFile(lesson.video_url) ? (
          <video
            key={lesson.id}
            src={buildAssetUrl(lesson.video_url)}
            className="w-full h-full"
            controls
            controlsList="nodownload"
            playsInline
            preload="metadata"
            data-testid="lesson-video-player"
          >
            Your browser does not support the video tag.
          </video>
        ) : (
          <iframe
            key={lesson.id}
            src={getEmbedUrl(lesson.video_url)}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            title={lesson.title}
            data-testid="lesson-video-iframe"
          />
        )
      ) : lesson?.content_type === 'pdf' && lesson.pdf_url ? (
        <iframe src={buildAssetUrl(lesson.pdf_url)} className="w-full h-full bg-white" title={lesson.title} />
      ) : lesson?.text_content ? (
        <div className="p-8 h-full overflow-y-auto bg-brand-surface">
          <p className="text-foreground whitespace-pre-wrap">{lesson.text_content}</p>
        </div>
      ) : (
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">
              {lesson ? 'No content available for this lesson' : 'Select a lesson to start watching'}
            </p>
          </div>
        </div>
      )}
    </div>

    {lesson && (
      <div className="p-4 bg-brand-surface border-t border-white/5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-white truncate" data-testid="now-playing-title">{lesson.title}</h2>
            <p className="text-xs text-gray-400 flex items-center gap-1.5">
              {lesson.duration_minutes} min
              {lesson.is_completed && (
                <span className="text-emerald-400 inline-flex items-center gap-1 ml-2">
                  <CheckCircle className="w-3 h-3" /> Completed
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <ReportButton videoType="lesson" videoId={lesson.id} variant="pill" />
            <Button
              variant="outline"
              size="sm"
              onClick={onPrev}
              disabled={!hasPrev}
              className="border-white/10 text-white hover:bg-white/5"
              data-testid="prev-lesson-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            {lesson.has_quiz && (
              <Button
                size="sm"
                onClick={() => onStartQuiz(lesson.quiz_id)}
                className="bg-brand-gold/15 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/25"
                data-testid="take-quiz-btn"
              >
                <FileQuestion className="w-4 h-4 mr-1" /> Quiz
              </Button>
            )}
            {!lesson.is_completed ? (
              <Button
                size="sm"
                onClick={() => onComplete(lesson)}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                data-testid="complete-lesson-btn"
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Complete{hasNext ? ' & Next' : ''}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={onNext}
                disabled={!hasNext}
                className="bg-white text-black hover:bg-gray-200"
                data-testid="next-lesson-btn"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
);
